import {
  BaseQueryFn,
  MutationDefinition,
  EndpointBuilder,
  QueryDefinition,
  InfiniteQueryDefinition,
} from '@reduxjs/toolkit/query';

import { transformCollectionResponse } from 'services/teamHeroApi.helpers';
import type {
  IGetMessagesArgs,
  IPostMessageArgs,
  IPutMessageArgs,
} from './messages.api.types';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import type {
  ICollection,
  ICollectionApiResponse,
  IInfinitePaginationArgs,
} from 'services/api/api.types';
import type { IMessage } from 'interfaces/Message.interface';

export const getMessages = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetMessagesArgs,
  BaseQueryFn,
  string,
  ICollection<IMessage>
> =>
  build.query<ICollection<IMessage>, IGetMessagesArgs>({
    providesTags: ['Message'],
    query: (queryArg: IGetMessagesArgs) => {
      const paginationParamObject = {
        conversation: queryArg?.conversation,
        page: queryArg?.page,
        itemsPerPage: queryArg?.itemsPerPage,
        pagination: queryArg?.pagination,
        ...queryArg?.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg?.filters);
      return {
        url: `/api/messages${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IMessage>
    ): ICollection<IMessage> => transformCollectionResponse<IMessage>(response),
  });

export const getInfiniteMessages = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): InfiniteQueryDefinition<
  IGetMessagesArgs,
  IInfinitePaginationArgs,
  BaseQueryFn,
  string,
  ICollection<IMessage>
> =>
  build.infiniteQuery<
    ICollection<IMessage>,
    IGetMessagesArgs,
    IInfinitePaginationArgs
  >({
    providesTags: ['Message'],
    infiniteQueryOptions: {
      // Must provide a default initial page param value
      initialPageParam: {
        page: 1,
        itemsPerPage: 10,
      },
      getNextPageParam: (
        lastPage,
        _allPages,
        { itemsPerPage, page: pageLastPage },
        _allPageParams
      ) => {
        const totalCount = lastPage?.totalCount;
        if (!totalCount || !itemsPerPage) {
          return undefined;
        }
        if (totalCount <= itemsPerPage * pageLastPage || totalCount === 0) {
          return undefined;
        }
        return { page: pageLastPage + 1, itemsPerPage };
      },
      getPreviousPageParam: (
        _firstPage,
        _allPages,
        { page: pageFirstPage, itemsPerPage },
        _allPageParams
      ) => {
        const prevPage = pageFirstPage - 1;
        if (prevPage < 1) {
          return undefined;
        }
        return { page: prevPage, itemsPerPage };
      },
    },
    query: ({ queryArg, pageParam: { page, itemsPerPage } }) => {
      const paginationParamObject = {
        page,
        itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return `api/messages${paginationQuery}${filterQuery}`;
    },
    transformResponse: (
      response: ICollectionApiResponse<IMessage>
    ): ICollection<IMessage> => transformCollectionResponse<IMessage>(response),
  });

export const getMessage = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<number, BaseQueryFn, string, IMessage> =>
  build.query<IMessage, number>({
    providesTags: ['Message'],
    query: (id) => {
      return {
        url: `/api/messages/${id}`,
      };
    },
  });

export const postMessage = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IPostMessageArgs, BaseQueryFn, string, IMessage> =>
  build.mutation<IMessage, IPostMessageArgs>({
    invalidatesTags: ['Message', 'MessageConversation'],
    query: (queryArg) => ({
      url: '/api/messages',
      method: 'POST',
      body: queryArg.body,
    }),
  });

export const putMessage = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IPutMessageArgs, BaseQueryFn, string, IMessage> =>
  build.mutation<IMessage, IPutMessageArgs>({
    invalidatesTags: ['Message', 'MessageConversation'],
    query: (queryArg) => ({
      url: `/api/messages/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const deleteMessage = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<number, BaseQueryFn, string, void> =>
  build.mutation<void, number>({
    invalidatesTags: ['Message'],
    query: (id) => ({
      url: `/api/messages/${id}`,
      method: 'DELETE',
    }),
  });
